import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { Alert, AlertType } from '@app/_models';

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  private subject = new Subject<Alert>();
  private defaultId = 'default-alert';
  private recentAlerts = new Map<string, number>(); // key: message+type, value: timestamp
  private deduplicationWindow = 3000; // 3 seconds - prevent showing same alert within this window

  constructor() { }

  // enable subscribing to alerts observable
  onAlert(id = this.defaultId): Observable<Alert> {
    return this.subject.asObservable().pipe(filter(x => x && x.id === id));
  }

  // convenience methods
  success(message: string, options?: any) {
      this.alert(new Alert({ ...options, type: AlertType.Success, message }));
  }

  error(message: string, options?: any) {
      this.alert(new Alert({ ...options, type: AlertType.Error, message }));
  }

  info(message: string, options?: any) {
      this.alert(new Alert({ ...options, type: AlertType.Info, message }));
  }

  warn(message: string, options?: any) {
      this.alert(new Alert({ ...options, type: AlertType.Warning, message }));
  }

  // main alert method
  alert(alert: Alert) {
      alert.id = alert.id || this.defaultId;

      // Create unique key for this alert (message + type)
      const alertKey = `${alert.type}:${alert.message}`;
      const now = Date.now();

      // Check if we recently showed this exact alert
      const lastShown = this.recentAlerts.get(alertKey);
      if (lastShown && (now - lastShown) < this.deduplicationWindow) {
        // Skip duplicate alert - it was shown recently
        return;
      }

      // Show the alert and track it
      this.recentAlerts.set(alertKey, now);
      this.subject.next(alert);

      // Clean up old entries to prevent memory leaks
      this.cleanupOldAlerts(now);
  }

  private cleanupOldAlerts(currentTime: number) {
    // Remove entries older than the deduplication window
    for (const [key, timestamp] of this.recentAlerts.entries()) {
      if (currentTime - timestamp > this.deduplicationWindow) {
        this.recentAlerts.delete(key);
      }
    }
  }

  // clear alerts
  clear(id = this.defaultId) {
      this.subject.next(new Alert({ id }));
  }
}
